import { HoverCard, Group, Stack, Text, Badge, Anchor, Box } from "@mantine/core";
import { IconMapPin, IconMail, IconPhone } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { CandidateAvatar } from "../Candidates/CandidateAvatar";
import LanguageBadges from "./LanguageBadges";
import { formatCandidateName } from "../_Shared/candidateName";
import { candidateRoleAndCompany } from "../_Shared/candidateRole";
import CompanyLink from "./CompanyLink";
import { candidateLocation } from "../_Shared/candidateLocation";
import { useStore } from "../store";

// A consistent, richer contact preview shown when hovering a candidate's avatar — same component
// everywhere an avatar appears (search results, contact list) so the gesture behaves identically.
// Renders ONLY data already loaded with the row (no fetch on hover): photo is reused from the
// page-wide avatar cache, the rest comes straight off the candidate object. The dropdown is
// portalled, so clicking a mail/phone link inside it never triggers the underlying row navigation.
export default function CandidateHoverCard({ candidate, children, disabled = false }) {
  const { t } = useTranslation("Veft");
  const nameFormat = useStore((s) => s.nameFormat);

  if (disabled || !candidate) return children;

  const { role, company, companyId, isFormer } = candidateRoleAndCompany(candidate);
  const roleLabel = isFormer && role ? `${t("role_former_prefix")} ${role}` : role;
  const location = candidateLocation(candidate);

  const emails = candidate.emails?.length
    ? candidate.emails
    : candidate.email
      ? [{ address: candidate.email }]
      : [];
  const phones = candidate.phoneNumbers?.length
    ? candidate.phoneNumbers
    : candidate.phone
      ? [{ number: candidate.phone }]
      : [];
  const primaryEmail = emails.find((e) => e.isPrimary) ?? emails[0];
  const primaryPhone = phones.find((p) => p.isPrimary) ?? phones[0];

  const skills = (candidate.skills ?? []).slice(0, 6);
  const tags = (candidate.tags ?? []).slice(0, 6);

  return (
    <HoverCard width={320} shadow="md" openDelay={250} closeDelay={100} position="right-start" withArrow withinPortal>
      <HoverCard.Target>
        <Box style={{ display: "inline-flex" }}>{children}</Box>
      </HoverCard.Target>
      <HoverCard.Dropdown onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
        <Stack gap="xs">
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <CandidateAvatar candidate={candidate} size="lg" radius="xl" color="blue" style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text fw={600} size="sm" style={{ lineHeight: 1.25 }}>
                {formatCandidateName(candidate, nameFormat)}
              </Text>
              {role && (
                <Text size="xs" c="dimmed">
                  {roleLabel}{company ? " · " : ""}
                  <CompanyLink company={company} companyId={companyId} />
                </Text>
              )}
              {candidate.openToWork && (
                <Badge size="xs" color="teal" variant="light" mt={4}>{t("open_to_work")}</Badge>
              )}
            </Box>
          </Group>

          {location && (
            <Group gap={4} wrap="nowrap" c="dimmed">
              <IconMapPin size={13} style={{ flexShrink: 0 }} />
              <Text size="xs">{location}</Text>
            </Group>
          )}

          {primaryEmail && (
            <Group gap={4} wrap="nowrap">
              <IconMail size={13} style={{ flexShrink: 0, color: "var(--mantine-color-dimmed)" }} />
              <Anchor href={`mailto:${primaryEmail.address}`} size="xs" style={{ minWidth: 0 }} truncate>
                {primaryEmail.address}
              </Anchor>
            </Group>
          )}
          {primaryPhone && (
            <Group gap={4} wrap="nowrap">
              <IconPhone size={13} style={{ flexShrink: 0, color: "var(--mantine-color-dimmed)" }} />
              <Anchor href={`tel:${primaryPhone.number}`} size="xs" style={{ fontVariantNumeric: "tabular-nums" }}>
                {primaryPhone.number}
              </Anchor>
            </Group>
          )}

          {(candidate.languages ?? []).length > 0 && (
            <Box>
              <Text size="xs" c="dimmed" mb={2}>{t("languages_label")}</Text>
              <LanguageBadges languages={candidate.languages} />
            </Box>
          )}

          {skills.length > 0 && (
            <Group gap={4} wrap="wrap">
              {skills.map((s) => (
                <Badge key={s} size="xs" variant="light" color="blue" tt="none">{s}</Badge>
              ))}
            </Group>
          )}
          {tags.length > 0 && (
            <Group gap={4} wrap="wrap">
              {tags.map((tg) => (
                <Badge key={tg} size="xs" variant="light" tt="none">{tg}</Badge>
              ))}
            </Group>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
